import { errorMessage } from "@atlas/shared";
import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, CalendarClock, X, Trash2 } from "lucide-react";
import {
  fetchAdminEvents,
  createAdminEvent,
  patchAdminEvent,
  deleteAdminEvent,
  type AdminEvent,
  type AdminEventRuleType,
} from "../../api/admin-content";
import { fetchAdminStoreItems, type AdminStoreItem } from "../../api/admin";
import { Badge } from "../../components/Badge";
import { Button } from "../../components/Button";
import { Skel } from "../../components/Skeleton";
import { EmptyState } from "../../components/EmptyState";
import { rarityColor, rarityLabel } from "../../lib/rarity";
import { useAuth } from "../../hooks/useAuth";
import { AdminShell, AccessDenied } from "./AdminTabs";

const inputStyle: React.CSSProperties = {
  width: "100%",
  height: 32,
  padding: "0 10px",
  background: "var(--surface-sunken)",
  border: "1px solid var(--border-default)",
  borderRadius: "var(--radius-md)",
  color: "var(--text-primary)",
  fontSize: 13,
  outline: "none",
  boxSizing: "border-box",
};

const label: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: "var(--text-secondary)",
  display: "block",
  marginBottom: 6,
};

// Mirrors packages/db/src/events.ts EVENT_RULE_TYPES / validateEventRule —
// window-scoped rules, distinct from the lifetime achievement rule engine.
const RULE_TYPES: { value: AdminEventRuleType; label: string }[] = [
  { value: "peak_count", label: "Peaks summited" },
  { value: "trail_section_count", label: "Trail sections completed" },
  { value: "landmark_count", label: "Landmarks visited" },
  { value: "total_distance", label: "Total distance" },
];

const REWARD_KINDS = ["badge", "banner", "title"] as const;

type EventStatus = "active" | "upcoming" | "ended" | "inactive";

function eventStatus(e: Pick<AdminEvent, "active" | "startsAt" | "endsAt">): EventStatus {
  if (!e.active) return "inactive";
  const now = Date.now();
  const starts = new Date(e.startsAt).getTime();
  const ends = new Date(e.endsAt).getTime();
  if (now < starts) return "upcoming";
  if (now > ends) return "ended";
  return "active";
}

const STATUS_BADGE: Record<EventStatus, { variant: "gold" | "info" | "neutral"; label: string }> = {
  active: { variant: "gold", label: "Active" },
  upcoming: { variant: "info", label: "Upcoming" },
  ended: { variant: "neutral", label: "Ended" },
  inactive: { variant: "neutral", label: "Inactive" },
};

function formatDateShort(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

// <input type="datetime-local"> works in the browser's local wall-clock time
// with no timezone designator. Converting a stored UTC ISO string to that
// format needs the getTimezoneOffset correction so edit → save → reopen
// round-trips to the same displayed time.
function isoToDatetimeLocal(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

function ruleParamValue(ruleType: AdminEventRuleType, ruleParams: Record<string, unknown>): string {
  if (ruleType === "total_distance") {
    const m = Number(ruleParams["distanceM"]);
    return Number.isFinite(m) ? String(m / 1000) : "";
  }
  const c = Number(ruleParams["count"]);
  return Number.isFinite(c) ? String(c) : "";
}

function buildRuleParams(ruleType: AdminEventRuleType, value: string): Record<string, unknown> {
  if (ruleType === "total_distance") {
    return { distanceM: Math.round((parseFloat(value) || 0) * 1000) };
  }
  return { count: parseInt(value, 10) || 0 };
}

// ─── Reward items picker ────────────────────────────────────────────────────

function RewardItemsPicker({
  selected,
  onToggle,
}: {
  selected: string[];
  onToggle: (id: string) => void;
}) {
  const { data, isLoading } = useQuery({ queryKey: ["admin/store/items"], queryFn: fetchAdminStoreItems });
  const items = (data?.items ?? []).filter((i) => (REWARD_KINDS as readonly string[]).includes(i.kind));

  if (isLoading) return <Skel height={80} />;

  if (items.length === 0) {
    return <p style={{ fontSize: 12, color: "var(--text-muted)", margin: 0 }}>No cosmetic store items available.</p>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 220, overflow: "auto" }}>
      {items.map((item: AdminStoreItem) => {
        const on = selected.includes(item.id);
        return (
          <button
            key={item.id}
            type="button"
            onClick={() => onToggle(item.id)}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
              padding: "7px 9px",
              borderRadius: "var(--radius-md)",
              border: `1px solid ${on ? "var(--border-gold)" : "var(--border-subtle)"}`,
              background: on ? "var(--accent-soft)" : "var(--surface-sunken)",
              cursor: "pointer",
              textAlign: "left",
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {item.name}
              </div>
              <div className="eyebrow" style={{ fontSize: 9.5, marginTop: 2 }}>{item.kind}</div>
            </div>
            <span style={{ fontSize: 11, fontWeight: 700, color: rarityColor(item.rarity), flex: "0 0 auto" }}>
              {rarityLabel(item.rarity)}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ─── Event drawer (create + edit) ──────────────────────────────────────────

function EventDrawer({ event, onClose }: { event: AdminEvent | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const isCreate = event === null;

  const [name, setName] = React.useState(event?.name ?? "");
  const [slug, setSlug] = React.useState(event?.slug ?? "");
  const [description, setDescription] = React.useState(event?.description ?? "");
  const [startsAt, setStartsAt] = React.useState(event ? isoToDatetimeLocal(event.startsAt) : "");
  const [endsAt, setEndsAt] = React.useState(event ? isoToDatetimeLocal(event.endsAt) : "");
  const [ruleType, setRuleType] = React.useState<AdminEventRuleType>(event?.ruleType ?? "peak_count");
  const [paramValue, setParamValue] = React.useState(
    event ? ruleParamValue(event.ruleType, event.ruleParams) : "",
  );
  const [rewardWaypoints, setRewardWaypoints] = React.useState(String(event?.rewardWaypoints ?? 0));
  const [rewardItemIds, setRewardItemIds] = React.useState<string[]>(event?.rewardItemIds ?? []);
  const [active, setActive] = React.useState(event?.active ?? true);
  const [confirmDelete, setConfirmDelete] = React.useState(false);

  function handleRuleTypeChange(next: AdminEventRuleType) {
    setRuleType(next);
    setParamValue("");
  }

  function toggleReward(id: string) {
    setRewardItemIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ["admin/content/events"] });

  const createMutation = useMutation({
    mutationFn: () =>
      createAdminEvent({
        slug: slug.trim() || undefined,
        name: name.trim(),
        description: description.trim(),
        startsAt: new Date(startsAt).toISOString(),
        endsAt: new Date(endsAt).toISOString(),
        ruleType,
        ruleParams: buildRuleParams(ruleType, paramValue),
        rewardItemIds,
        rewardWaypoints: parseInt(rewardWaypoints, 10) || 0,
        active,
      }),
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });

  const patchMutation = useMutation({
    mutationFn: () => {
      if (!event) throw new Error("No event to update");
      return patchAdminEvent(event.id, {
        name: name.trim(),
        description: description.trim(),
        startsAt: new Date(startsAt).toISOString(),
        endsAt: new Date(endsAt).toISOString(),
        ruleType,
        ruleParams: buildRuleParams(ruleType, paramValue),
        rewardItemIds,
        rewardWaypoints: parseInt(rewardWaypoints, 10) || 0,
        active,
      });
    },
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (force: boolean) => {
      if (!event) throw new Error("No event to delete");
      return deleteAdminEvent(event.id, force);
    },
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });

  const busy = createMutation.isPending || patchMutation.isPending;
  const err = createMutation.error ?? patchMutation.error ?? deleteMutation.error;

  function handleDeleteClick() {
    if (!event) return;
    if (event.completions > 0 && !confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    deleteMutation.mutate(event.completions > 0);
  }

  return (
    <div
      style={{ position: "fixed", inset: 0, zIndex: 1000, display: "flex", justifyContent: "flex-end" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div aria-hidden="true" style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,.45)" }} onClick={onClose} />
      <div
        style={{
          position: "relative",
          width: 460,
          maxWidth: "92vw",
          height: "100%",
          background: "var(--surface-card)",
          borderLeft: "1px solid var(--border-strong)",
          boxShadow: "var(--shadow-xl)",
          display: "flex",
          flexDirection: "column",
          overflow: "auto",
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", padding: "20px 22px 14px", borderBottom: "1px solid var(--border-subtle)" }}>
          <div>
            <p className="eyebrow" style={{ fontSize: 10, marginBottom: 4 }}>
              {isCreate ? "New event" : "Event"}
            </p>
            <div style={{ fontSize: 16, fontWeight: 600, color: "var(--text-primary)" }}>
              {event?.name ?? "Untitled"}
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", padding: 4 }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: "16px 22px 28px", display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <label style={label}>Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} placeholder="Summer Summits" />
          </div>

          {isCreate && (
            <div>
              <label style={label}>Slug (optional — derived from name)</label>
              <input value={slug} onChange={(e) => setSlug(e.target.value)} style={inputStyle} placeholder="summer-summits-2026" />
            </div>
          )}

          <div>
            <label style={label}>Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              style={{ ...inputStyle, height: "auto", padding: 8, resize: "vertical" }}
            />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={label}>Starts at</label>
              <input type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={label}>Ends at</label>
              <input type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} style={inputStyle} />
            </div>
          </div>

          <div style={{ height: 1, background: "var(--border-subtle)", margin: "2px 0" }} />

          <div style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr", gap: 12 }}>
            <div>
              <label style={label}>Rule type</label>
              <select
                value={ruleType}
                onChange={(e) => handleRuleTypeChange(e.target.value as AdminEventRuleType)}
                style={{ ...inputStyle, cursor: "pointer" }}
              >
                {RULE_TYPES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            <div>
              <label style={label}>{ruleType === "total_distance" ? "Distance (km)" : "Count"}</label>
              <input
                type="number"
                min={0}
                value={paramValue}
                onChange={(e) => setParamValue(e.target.value)}
                style={inputStyle}
              />
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, alignItems: "end" }}>
            <div>
              <label style={label}>Reward Waypoints</label>
              <input type="number" min={0} value={rewardWaypoints} onChange={(e) => setRewardWaypoints(e.target.value)} style={inputStyle} />
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 8, height: 32, cursor: "pointer" }}>
              <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
              <span style={{ fontSize: 13, color: "var(--text-secondary)" }}>Active</span>
            </label>
          </div>

          <div>
            <label style={label}>Reward items ({rewardItemIds.length} selected)</label>
            <RewardItemsPicker selected={rewardItemIds} onToggle={toggleReward} />
          </div>

          {err && (
            <p style={{ fontSize: 13, color: "var(--status-danger)", margin: 0 }}>
              {errorMessage(err, "Something went wrong")}
            </p>
          )}

          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <Button variant="ghost" onClick={onClose}>Close</Button>
            <Button
              variant="secondary"
              disabled={busy || !name.trim() || !description.trim() || !startsAt || !endsAt}
              onClick={() => (isCreate ? createMutation.mutate() : patchMutation.mutate())}
            >
              {busy ? "Saving…" : isCreate ? "Create event" : "Save changes"}
            </Button>
          </div>

          {event && (
            <div
              style={{
                padding: "12px 14px",
                border: "1px solid rgba(224, 80, 63, 0.35)",
                borderRadius: "var(--radius-md)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
              }}
            >
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}>Delete event</div>
                <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
                  {event.completions > 0
                    ? confirmDelete
                      ? `${event.completions} user(s) completed this — click again to force-delete (granted rewards are kept).`
                      : `${event.completions} user(s) completed this — click to confirm force-delete.`
                    : "No completions — safe to delete."}
                </div>
              </div>
              <Button variant="danger" size="sm" disabled={deleteMutation.isPending} onClick={handleDeleteClick} leftIcon={<Trash2 size={13} />}>
                Delete
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── AdminEventsPage ─────────────────────────────────────────────────────────

export default function AdminEventsPage() {
  const { user } = useAuth();
  const [drawerEvent, setDrawerEvent] = React.useState<AdminEvent | null | "new">(null);

  const eventsQuery = useQuery({ queryKey: ["admin/content/events"], queryFn: fetchAdminEvents });

  if (!user?.isAdmin) return <AccessDenied />;

  const rows = eventsQuery.data?.events ?? [];
  const gridColumns = "minmax(0,1.4fr) 90px minmax(0,1.1fr) 80px 90px";
  const headerCell: React.CSSProperties = {
    fontSize: 10.5,
    fontWeight: 600,
    color: "var(--text-muted)",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  };

  return (
    <AdminShell
      title="Events"
      action={
        <Button variant="secondary" size="sm" leftIcon={<Plus size={14} />} onClick={() => setDrawerEvent("new")}>
          New event
        </Button>
      }
    >
      <div
        style={{
          background: "var(--surface-card)",
          border: "1px solid var(--border-subtle)",
          borderRadius: "var(--radius-lg)",
          boxShadow: "var(--shadow-sm), var(--ring-top)",
          padding: "6px 18px 12px",
          overflowX: "auto",
        }}
      >
        <div style={{ display: "grid", gridTemplateColumns: gridColumns, gap: 10, padding: "10px 0 8px", borderBottom: "1px solid var(--border-subtle)", minWidth: 620 }}>
          <span style={headerCell}>Name</span>
          <span style={headerCell}>Status</span>
          <span style={headerCell}>Window</span>
          <span style={{ ...headerCell, textAlign: "right" }}>Completions</span>
          <span style={{ ...headerCell, textAlign: "right" }}>Waypoints</span>
        </div>

        {eventsQuery.isLoading ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "12px 0" }}>
            <Skel height={30} /><Skel height={30} /><Skel height={30} />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState icon={<CalendarClock size={20} />} title="No events yet" hint="Create the first one above." style={{ margin: "12px 0" }} />
        ) : (
          rows.map((row) => {
            const status = eventStatus(row);
            const statusStyle = STATUS_BADGE[status];
            return (
              <div
                key={row.id}
                onClick={() => setDrawerEvent(row)}
                style={{
                  display: "grid",
                  gridTemplateColumns: gridColumns,
                  gap: 10,
                  alignItems: "center",
                  padding: "9px 0",
                  borderBottom: "1px solid var(--border-subtle)",
                  cursor: "pointer",
                  minWidth: 620,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "var(--surface-hover)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: "var(--text-primary)", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {row.name}
                  </div>
                  <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-muted)" }}>{row.slug}</div>
                </div>
                <span><Badge variant={statusStyle.variant} dot={status === "active"}>{statusStyle.label}</Badge></span>
                <span style={{ fontSize: 12, color: "var(--text-secondary)", fontFamily: "var(--font-mono)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {formatDateShort(row.startsAt)} – {formatDateShort(row.endsAt)}
                </span>
                <span className="tnum" style={{ fontSize: 12, color: "var(--text-primary)", textAlign: "right" }}>{row.completions}</span>
                <span className="tnum" style={{ fontSize: 12, color: "var(--text-muted)", textAlign: "right" }}>{row.rewardWaypoints}</span>
              </div>
            );
          })
        )}
      </div>

      {drawerEvent !== null && (
        <EventDrawer
          event={drawerEvent === "new" ? null : drawerEvent}
          onClose={() => setDrawerEvent(null)}
        />
      )}
    </AdminShell>
  );
}
